/** Exit rules of a Manual trading strategy: close the position when the index candle closes (or the
 * price crosses) past the 9 EMA, the 50 EMA, the last swing, or a price typed in
 * (docs/manual_trading.md, "Exit rules"). Any one or several may be on. Nothing is sent while the
 * controls are changed: Save exit rules asks to confirm, and on Confirm the rules are saved and
 * applied to the running trade at once. A put mirrors them (above, not below).
 */

import { useState, type ReactElement } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import { describeError } from "@/lib/http";
import {
  TIMEFRAMES,
  manualRulesApi,
  type ExitRules,
  type RuleSource,
  type RuleMode,
  type RuleTimeframe,
} from "@/lib/manualRulesApi";
import type { Strategy } from "@/types/strategy";

const OFF: ExitRules = {
  timeframe: "5m",
  ema9: null,
  ema50: null,
  swing: null,
  swing_bars: 5,
  price: null,
  price_level: null,
  source: "index",
};

function toMode(value: string): RuleMode {
  return value === "close" || value === "cross" ? value : null;
}

function ModeSelect({
  label,
  value,
  onChange,
}: Readonly<{ label: string; value: RuleMode; onChange: (m: RuleMode) => void }>): ReactElement {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <select
        className="input"
        value={value ?? "off"}
        onChange={(e) => {
          onChange(toMode(e.target.value));
        }}
      >
        <option value="off">Off</option>
        <option value="close">Candle closes below</option>
        <option value="cross">Price crosses below</option>
      </select>
    </label>
  );
}

function NumberInput({
  label,
  value,
  onCommit,
}: Readonly<{
  label: string;
  value: number | null;
  onCommit: (n: number | null) => void;
}>): ReactElement {
  const [text, setText] = useState(value === null ? "" : String(value));
  const commit = () => {
    const n = Number(text);
    onCommit(text.trim() === "" || !Number.isFinite(n) || n <= 0 ? null : n);
  };
  return (
    <label className="block">
      <span className="label">{label}</span>
      <input
        type="number"
        className="input"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
        }}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
        }}
      />
    </label>
  );
}

type SetRules = (change: Partial<ExitRules>) => void;

const SOURCES: readonly (readonly [string, string])[] = [
  ["index", "Index"],
  ["option", "Traded option"],
  ["both", "Both (either breaks)"],
];

function ChoiceSelect({
  label,
  value,
  choices,
  onChange,
}: Readonly<{
  label: string;
  value: string;
  choices: readonly (readonly [string, string])[];
  onChange: (value: string) => void;
}>): ReactElement {
  return (
    <label className="block">
      <span className="label">{label}</span>
      <select
        className="input"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
      >
        {choices.map(([id, text]) => (
          <option key={id} value={id}>
            {text}
          </option>
        ))}
      </select>
    </label>
  );
}

function LineRules({ rules, set }: Readonly<{ rules: ExitRules; set: SetRules }>): ReactElement {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <ChoiceSelect
        label="Candles"
        value={rules.timeframe}
        choices={TIMEFRAMES.map((t) => [t, t])}
        onChange={(timeframe) => {
          set({ timeframe: timeframe as RuleTimeframe });
        }}
      />
      <ChoiceSelect
        label="Read on"
        value={rules.source}
        choices={SOURCES}
        onChange={(source) => {
          set({ source: source as RuleSource });
        }}
      />
      <ModeSelect
        label="9 EMA"
        value={rules.ema9}
        onChange={(ema9) => {
          set({ ema9 });
        }}
      />
      <ModeSelect
        label="50 EMA"
        value={rules.ema50}
        onChange={(ema50) => {
          set({ ema50 });
        }}
      />
      <ModeSelect
        label="Swing"
        value={rules.swing}
        onChange={(swing) => {
          set({ swing });
        }}
      />
      <NumberInput
        label="Swing bars each side"
        value={rules.swing_bars}
        onCommit={(n) => {
          if (n !== null && Number.isInteger(n) && n >= 2 && n <= 20) set({ swing_bars: n });
        }}
      />
    </div>
  );
}

function PriceRule({ rules, set }: Readonly<{ rules: ExitRules; set: SetRules }>): ReactElement {
  return (
    <div className="mt-3 flex flex-wrap items-end gap-3">
      <ModeSelect
        label="Price"
        value={rules.price}
        onChange={(price) => {
          set({ price });
        }}
      />
      <NumberInput
        label="Price level"
        value={rules.price_level}
        onCommit={(price_level) => {
          set({ price_level });
        }}
      />
    </div>
  );
}

const MODE_WORDS = { close: "candle closes below", cross: "price crosses below" } as const;

/** The rules that are on, one line each, for the confirmation. */
function describeRules(r: ExitRules): string[] {
  const word = (m: RuleMode) => (m === null ? null : MODE_WORDS[m]);
  const lines: [string, RuleMode, string][] = [
    ["9 EMA", r.ema9, ""],
    ["50 EMA", r.ema50, ""],
    ["Swing low", r.swing, ` (${String(r.swing_bars)} candles each side)`],
    [`Price ${String(r.price_level ?? "")}`, r.price, ""],
  ];
  const on = {
    index: "the index",
    option: "the traded option",
    both: "the index and the traded option",
  };
  return lines.flatMap(([name, mode, extra]) =>
    word(mode) === null ? [] : [`${name}: ${word(mode) ?? ""}${extra}, read on ${on[r.source]}`],
  );
}

function useSaveRules(broker: string, onDone: (note: string | null) => void) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rules: ExitRules) => manualRulesApi.put(broker, rules),
    onSuccess: (kept) => {
      qc.setQueryData(["manual-exit-rules", broker], kept);
      onDone("Saved and applied to the running trade now.");
    },
    onError: (err) => {
      onDone(`Not saved: ${describeError(err)}`);
    },
  });
}

function SaveBar({
  dirty,
  blocked,
  onSave,
  onDiscard,
}: Readonly<{
  dirty: boolean;
  blocked: boolean;
  onSave: () => void;
  onDiscard: () => void;
}>): ReactElement {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      <button
        type="button"
        className="btn btn-primary"
        disabled={!dirty || blocked}
        onClick={onSave}
      >
        <i className="ti ti-device-floppy text-base" />
        Save exit rules
      </button>
      <button type="button" className="btn" disabled={!dirty} onClick={onDiscard}>
        Discard changes
      </button>
    </div>
  );
}

function ConfirmRules({
  open,
  rules,
  busy,
  onCancel,
  onConfirm,
}: Readonly<{
  open: boolean;
  rules: ExitRules;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}>): ReactElement {
  const lines = describeRules(rules);
  return (
    <ConfirmDialog
      open={open}
      title="Apply these exit rules?"
      description={
        <ul className="list-disc pl-5">
          {lines.length === 0 ? (
            <li>No rule: every exit rule is switched off.</li>
          ) : (
            lines.map((line) => <li key={line}>{line}</li>)
          )}
          <li>
            Candles of {rules.timeframe}; they apply to the running trade as soon as you confirm.
          </li>
        </ul>
      }
      confirmLabel="Confirm and apply"
      busy={busy}
      onCancel={onCancel}
      onConfirm={onConfirm}
    />
  );
}

function RulesTitle(): ReactElement {
  return (
    <div className="mb-2 flex items-center gap-2">
      <i className="ti ti-door-exit text-accent" />
      <span className="text-sm font-medium">Exit rules</span>
    </div>
  );
}

/** What the rules do, and what is wrong or was done. */
function RulesNotes({
  missingLevel,
  error,
  note,
}: Readonly<{ missingLevel: boolean; error: string | null; note: string | null }>): ReactElement {
  return (
    <>
      <p className="mt-2 text-[11px] text-ink-muted">
        The candles of the size above are read on the index, on the traded option, or on both (the
        swing is the last low with that many candles on each side; on the index a put mirrors every
        rule: above, not below; on the option a fall is always the bad side). Any rule that breaks
        closes the whole position. A rule counts only candles that finish after you save it, and a
        cross only after the price has been on the right side of the line once. Checked every 5
        seconds.
      </p>
      {missingLevel && (
        <p className="mt-1 text-xs text-ink-secondary">The price rule needs a price level.</p>
      )}
      {error !== null && <p className="mt-1 text-xs text-ink-secondary">Rules not read: {error}</p>}
      {note !== null && <p className="mt-1 text-xs text-ink-secondary">{note}</p>}
    </>
  );
}

/** The rules being edited (a draft apart from what is saved), the confirmation, and the save. */
function useRulesForm(strategy: Strategy) {
  const query = useQuery({
    queryKey: ["manual-exit-rules", strategy.broker],
    queryFn: () => manualRulesApi.get(strategy.broker),
  });
  const [draft, setDraft] = useState<ExitRules | null>(null);
  const [asking, setAsking] = useState(false);
  const [fresh, setFresh] = useState(0);
  const [note, setNote] = useState<string | null>(null);
  const save = useSaveRules(strategy.broker, (text) => {
    setNote(text);
    setAsking(false);
    if (text?.startsWith("Saved")) setDraft(null);
  });
  const rules = draft ?? query.data ?? OFF;
  const set: SetRules = (change) => {
    setNote(null);
    setDraft({ ...rules, ...change });
  };
  const discard = () => {
    setDraft(null);
    setFresh((n) => n + 1);
    setNote(null);
  };
  return { query, draft, asking, setAsking, fresh, note, save, rules, set, discard };
}

export function ManualExitRulesCard({ strategy }: Readonly<{ strategy: Strategy }>): ReactElement {
  const { query, draft, asking, setAsking, fresh, note, save, rules, set, discard } =
    useRulesForm(strategy);
  const missingLevel = rules.price !== null && rules.price_level === null;
  return (
    <section className="card mb-3">
      <RulesTitle />
      <LineRules key={`l${String(fresh)}`} rules={rules} set={set} />
      <PriceRule key={`p${String(fresh)}`} rules={rules} set={set} />
      <SaveBar
        dirty={draft !== null}
        blocked={missingLevel || save.isPending}
        onSave={() => {
          setAsking(true);
        }}
        onDiscard={discard}
      />
      <RulesNotes
        missingLevel={missingLevel}
        error={query.isError ? describeError(query.error) : null}
        note={note}
      />
      <ConfirmRules
        open={asking}
        rules={rules}
        busy={save.isPending}
        onCancel={() => {
          setAsking(false);
        }}
        onConfirm={() => {
          if (draft !== null) save.mutate(draft);
        }}
      />
    </section>
  );
}
